import React, { useMemo } from 'react';
import { Badge, Flex, Text } from '@/components/ui';
import { IconArrowRight } from '@tabler/icons-react';
import { StudyFormHoverCard } from './StudyFormHoverCard';
import { FormIcon } from '@/pages/data-import/components/FormIcon';
import { getFormDisplayName, getFormTypeLabel } from '@/utils/formUtils';
import type { StudyForm } from '@/types/studyForms';

interface FormDisplayProps {
  form: StudyForm;
  allForms?: Map<number, StudyForm>;
  showIcon?: boolean;
  showBadge?: boolean;
  showParent?: boolean; // Show parent form if exists
  useName?: boolean; // Show form.name instead of displayName
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'compact' | 'detailed';
  className?: string;
}

// Helper function to find parent form
const findParentForm = (
  form: StudyForm,
  allForms: Map<number, StudyForm>
): StudyForm | null => {
  if (!allForms || allForms.size === 0) {
    return null;
  }

  // Search through all forms to find one that has this form as a subform
  for (const [, potentialParent] of allForms) {
    if (
      potentialParent.json_schema?.subFormArray &&
      Array.isArray(potentialParent.json_schema.subFormArray)
    ) {
      if (potentialParent.json_schema.subFormArray.includes(form.name)) {
        return potentialParent;
      }
    }
  }

  return null;
};

// Size mappings
const sizeConfig = {
  small: {
    iconSize: 16,
    textSize: '1' as const,
    badgeSize: 'xsmall' as const,
  },
  medium: {
    iconSize: 18,
    textSize: '2' as const,
    badgeSize: 'small' as const,
  },
  large: {
    iconSize: 20,
    textSize: '3' as const,
    badgeSize: 'small' as const,
  },
};

export const FormDisplay: React.FC<FormDisplayProps> = ({
  form,
  allForms,
  showIcon = true,
  showBadge = false,
  showParent = true,
  useName = false,
  size = 'medium',
  variant = 'default',
  className,
}) => {
  const config = sizeConfig[size];
  const formTypeLabel = getFormTypeLabel(form.type);
  const displayName = useName ? form.name : getFormDisplayName(form);
  const fieldCount = form.json_schema?.fieldsArray?.length || 0;

  // Convert Map to Array for FormIcon component
  const availableFormsArray = useMemo(() => {
    if (!allForms) return undefined;
    return Array.from(allForms.values());
  }, [allForms]);

  // Find parent form if exists
  const parentForm = useMemo(() => {
    if (!allForms || !showParent) return null;
    return findParentForm(form, allForms);
  }, [form, allForms, showParent]);

  // Render content
  const content = (
    <Flex
      align="center"
      gap="2"
      className={className}
      style={{
        cursor: 'pointer',
        width: '100%',
        flex: '1',
        minWidth: 0, // Allow flex shrinking for truncation
      }}
    >
      {showIcon && (
        <div style={{ flexShrink: 0 }}>
          <FormIcon
            form={form}
            availableForms={availableFormsArray}
            size={config.iconSize}
          />
        </div>
      )}

      {/* Breadcrumbs: Show parent form if exists with truncation */}
      {parentForm && showParent && (
        <Flex
          align="center"
          gap="1"
          style={{
            flexShrink: 1,
            minWidth: 0,
            maxWidth: '40%', // Limit parent width to ensure focus on target form
          }}
        >
          <Text
            size={config.textSize === '1' ? '1' : '2'}
            color="gray"
            style={{
              opacity: 0.7,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={useName ? parentForm.name : getFormDisplayName(parentForm)}
          >
            {useName ? parentForm.name : getFormDisplayName(parentForm)}
          </Text>
          <IconArrowRight
            size={config.iconSize - 4}
            style={{
              color: 'var(--gray-9)',
              opacity: 0.5,
              flexShrink: 0,
            }}
          />
        </Flex>
      )}

      {/* Target form name - emphasized, truncated if needed */}
      <Text
        size={config.textSize}
        weight="medium"
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={displayName}
      >
        {displayName}
      </Text>

      {showBadge && (
        <Badge
          color="info"
          variant="soft"
          size={config.badgeSize}
          style={{ flexShrink: 0 }}
        >
          {formTypeLabel}
        </Badge>
      )}

      {variant === 'detailed' &&
        form.json_schema?.displayName &&
        form.json_schema.displayName !== form.name &&
        !useName && (
          <Text
            size="1"
            color="gray"
            style={{
              fontFamily: 'var(--font-mono)',
              flexShrink: 0,
            }}
          >
            {form.name}
          </Text>
        )}

      {variant === 'detailed' && fieldCount > 0 && (
        <Text size="1" color="gray" style={{ flexShrink: 0 }}>
          ({fieldCount} {fieldCount === 1 ? 'field' : 'fields'})
        </Text>
      )}
    </Flex>
  );

  // Wrap with HoverCard for interactive form display
  return (
    <StudyFormHoverCard form={form} fieldCount={fieldCount} allForms={allForms}>
      {content}
    </StudyFormHoverCard>
  );
};
